import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './asset-modal.style.js';
import { createAsset, getLocationsByCompany, getLocations, updateAsset } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { PhotoUploadService } from '../../services/photo-upload.service.js';

@customElement('asset-modal')
export class AssetModal extends LitElement {
  @property({ type: Boolean })
  open = false;

  @state()
  private formData: {
    name: string;
    location_id: string;
    cost: number | null;
    purchase_date: number | null;
    warranty_date: number | null;
    photo_url: string | null;
  } = {
    name: '',
    location_id: '',
    cost: null,
    purchase_date: null,
    warranty_date: null,
    photo_url: null
  };

  @state()
  private selectedPhotoFile: File | null = null;

  @state()
  private locations: Array<{ id: string; model?: { name: string } }> = [];

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';
  
  @state()
  private showQRCode = false;
  
  @state()
  private createdAsset: any = null;
  
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  
  static styles = styles;

  connectedCallback() {
    super.connectedCallback();
    this.loadLocations();
  }

  private async loadLocations() {
    try {
      // First try to get locations by company
      let result = await getLocationsByCompany(this.firestore, this.companyId);
      
      // If no results, try to get all locations
      if (!result.data || result.data.length === 0) {
        result = await getLocations(this.firestore);
      }
      
      if (result.data) {
        this.locations = result.data;
      } else {
        this.errorMessage = result.error || 'Failed to load locations';
      }
    } catch (error) {
      this.errorMessage = 'Failed to load locations. Please try again.';
    }
  }

  private handleInputChange(e: Event, field: string) {
    const target = e.target as HTMLInputElement;
    let value: any = target.value;
    
    // Handle different input types
    if (field === 'cost') {
      value = value === '' ? null : parseFloat(value);
    } else if (field === 'purchase_date' || field === 'warranty_date') {
      value = value === '' ? null : new Date(value).getTime();
    }
    
    this.formData = {
      ...this.formData,
      [field]: value
    };
  }

  private handleDateChange(e: Event, field: string) {
    const target = e.target as HTMLInputElement;
    const value = target.value === '' ? null : new Date(target.value).getTime();
    
    this.formData = {
      ...this.formData,
      [field]: value
    };
  }

  private formatDateForInput(timestamp: number | null): string {
    if (!timestamp) return '';
    return new Date(timestamp).toISOString().split('T')[0];
  }

  private handleLocationChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    this.formData = {
      ...this.formData,
      location_id: target.value
    };
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    
    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Create the asset first
      const assetData = {
        name: this.formData.name,
        company_id: this.companyId,
        status: 'Active',
        location_id: this.formData.location_id,
        cost: this.formData.cost,
        purchase_date: this.formData.purchase_date,
        warranty_date: this.formData.warranty_date,
        photo_url: null // Will be updated after photo upload
      };
      
      const result = await createAsset(this.firestore, assetData);

      // Check if the response has data (successful creation)
      if (result.data) {
        const newAsset = result.data as any; // Type assertion to handle the asset object
        
        // If we have a photo file, upload it now
        if (this.selectedPhotoFile) {
          try {
            const photoUploadService = new PhotoUploadService();
            const photoResult = await photoUploadService.uploadPhoto(
              this.selectedPhotoFile,
              newAsset.id || newAsset.asset_id,
              'default'
            );
            
            if (photoResult.success && photoResult.url) {
              // Update the asset with the photo URL
              await updateAsset(this.firestore, newAsset.id || newAsset.asset_id, {
                photo_url: photoResult.url,
                updated_at: Date.now()
              });
              
              // Update the result data with the photo URL
              newAsset.photo_url = photoResult.url;
            }
          } catch (photoError) {
            console.error('Failed to upload photo:', photoError);
            // Continue with asset creation even if photo upload fails
          }
        }
        
        // Store the created asset for QR code display
        this.createdAsset = newAsset;
        this.showQRCode = true;
        
        // Notify parent but don't close modal yet - only dispatch asset-created
        // We'll dispatch 'saved' when user clicks Done to close the modal
        this.dispatchEvent(new CustomEvent('asset-created', {
          detail: newAsset,
          bubbles: true,
          composed: true
        }));
      } else {
        this.errorMessage = result.error || 'Failed to create asset';
      }
    } catch (error) {
      this.errorMessage = 'Failed to create asset. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private validateForm(): boolean {
    if (!this.formData.name.trim()) {
      this.errorMessage = 'Asset name is required';
      return false;
    }
    if (!this.formData.location_id) {
      this.errorMessage = 'Location is required';
      return false;
    }
    return true;
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
    this.formData = {
      name: '',
      location_id: '',
      cost: null,
      purchase_date: null,
      warranty_date: null,
      photo_url: null
    };
    this.selectedPhotoFile = null; // Reset selected photo file
    this.errorMessage = '';
    this.showQRCode = false;
    this.createdAsset = null;
  }

  private handleCancel() {
    this.closeModal();
    this.open = false;
  }

  private handleQRCodeClose() {
    // Dispatch 'saved' event to trigger assets page refresh
    this.dispatchEvent(new CustomEvent('saved', {
      detail: this.createdAsset,
      bubbles: true,
      composed: true
    }));
    
    this.closeModal();
    this.open = false;
  }

  private downloadQRCode() {
    if (this.createdAsset?.qr_code_url) {
      const link = document.createElement('a');
      link.href = this.createdAsset.qr_code_url;
      link.download = `asset-${this.createdAsset.id}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private handlePhotoUploaded(e: CustomEvent) {
    // Store the photo file for later upload
    this.selectedPhotoFile = e.detail.file;
    this.formData.photo_url = null; // Will be set after asset creation
  }

  private handlePhotoRemoved() {
    this.selectedPhotoFile = null;
    this.formData.photo_url = null;
  }

  private generateTempId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  render() {
    return html`
      <sl-dialog
        label="${this.showQRCode ? 'Asset Created Successfully!' : 'Add an Asset'}"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        <div class="modal-content">
          ${this.showQRCode && this.createdAsset ? html`
            <!-- QR Code Display Section -->
            <div class="success-section">
              <div class="success-header">
                <sl-icon name="check-circle" style="color: var(--sl-color-success-600); font-size: 2rem; margin-bottom: 0.5rem;"></sl-icon>
                <p style="margin: 0 0 1rem 0; color: var(--sl-color-success-700); font-weight: 600;">
                  "${this.createdAsset.name}" has been created successfully!
                </p>
              </div>

              ${this.createdAsset.qr_code_url ? html`
                <div class="qr-code-section">
                  <h4 style="margin: 0 0 0.75rem 0; color: var(--sl-color-neutral-700);">QR Code Generated</h4>
                  <div class="qr-code-container">
                    <img 
                      src="${this.createdAsset.qr_code_url}" 
                      alt="QR Code for ${this.createdAsset.name}"
                      style="width: 150px; height: 150px; border: 1px solid var(--sl-color-neutral-300); border-radius: 8px;"
                    />
                  </div>
                  <div class="qr-code-info">
                    <p style="margin: 0.5rem 0; color: var(--sl-color-neutral-600); font-size: 0.875rem;">
                      Scan this QR code to view asset details on mobile devices.
                    </p>
                    <div class="qr-actions">
                      <sl-button 
                        variant="neutral" 
                        size="small"
                        @click=${this.downloadQRCode}
                      >
                        <sl-icon name="download" slot="prefix"></sl-icon>
                        Download QR Code
                      </sl-button>
                    </div>
                  </div>
                </div>
              ` : html`
                <div class="qr-code-section">
                  <sl-alert variant="warning" open>
                    <sl-icon name="exclamation-triangle" slot="icon"></sl-icon>
                    QR code generation failed. The asset was created successfully.
                  </sl-alert>
                </div>
              `}

            </div>
          ` : html`
            <!-- Asset Creation Form -->
            ${this.errorMessage ? html`
              <div class="error-message">
                <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
                ${this.errorMessage}
              </div>
            ` : ''}

          <form @submit=${this.handleSubmit} 
            @click=${(e: Event) => e.stopPropagation()}
            @sl-input=${(e: Event) => e.stopPropagation()}
            @sl-change=${(e: Event) => e.stopPropagation()}
            @sl-open-change=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-hide=${(e: Event) => e.stopPropagation()}
          >
            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="name">Asset Name *</label>
              <sl-input
                id="name"
                name="name"
                placeholder="Enter asset name"
                value=${this.formData.name}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'name')}
                @click=${(e: Event) => e.stopPropagation()}
                required
                size="large"
              >
                <sl-icon name="box" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="location">Location *</label>
              
              <sl-select
                id="location"
                name="location"
                value=${this.formData.location_id}
                @sl-change=${(e: CustomEvent) => {
                  this.handleLocationChange(e);
                  e.stopPropagation();
                }}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
                required
              >
                <sl-option value="">Select a location</sl-option>
                ${this.locations.map(location => html`
                  <sl-option value=${location.id}>${location.model?.name || location.id}</sl-option>
                `)}
              </sl-select>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="cost">Cost</label>
              <sl-input
                id="cost"
                name="cost"
                type="number"
                placeholder="Enter cost"
                value=${this.formData.cost === null ? '' : this.formData.cost}
                @sl-input=${(e: Event) => this.handleInputChange(e, 'cost')}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-icon name="dollar-sign" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="purchase_date">Purchase Date</label>
              <sl-input
                id="purchase_date"
                name="purchase_date"
                type="date"
                value=${this.formatDateForInput(this.formData.purchase_date)}
                @sl-input=${(e: Event) => this.handleDateChange(e, 'purchase_date')}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-icon name="calendar" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label for="warranty_date">Warranty Date</label>
              <sl-input
                id="warranty_date"
                name="warranty_date"
                type="date"
                value=${this.formatDateForInput(this.formData.warranty_date)}
                @sl-input=${(e: Event) => this.handleDateChange(e, 'warranty_date')}
                @click=${(e: Event) => e.stopPropagation()}
                placeholder="Select warranty date"
              >
                <sl-icon name="shield" slot="prefix"></sl-icon>
              </sl-input>
            </div>

            <div class="form-group" @click=${(e: Event) => e.stopPropagation()}>
              <label>Asset Photo</label>
              <photo-upload
                .assetId=${this.generateTempId()}
                .companyId=${'default'}
                @photo-uploaded=${this.handlePhotoUploaded}
                @photo-removed=${this.handlePhotoRemoved}
              ></photo-upload>
            </div>

          </form>
          `}
        </div>

        <div slot="footer" class="dialog-footer">
          <sl-button variant="neutral" @click=${this.handleCancel}>Cancel</sl-button>
          <sl-button 
            variant="primary" 
            @click=${this.showQRCode ? this.handleQRCodeClose : this.handleSubmit}
            ?loading=${this.isLoading && !this.showQRCode}
            ?disabled=${this.isLoading && !this.showQRCode}
          >
            ${this.isLoading && !this.showQRCode ? 'Creating...' : this.showQRCode ? 'Done' : 'Create Asset'}
          </sl-button>
        </div>
      </sl-dialog>
    `;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'asset-modal': AssetModal;
  }
}
